import { OTHER_PAYMENT_OPTION_ID } from '@webapp/components/RequestableCreditCardSelect'
import { type ExpenseItemFormSchema } from '@webapp/modules/expense/components/ExpenseLineItemDrawer'
import { ExpensePaymentTypes } from '@webapp/modules/expense/entities/enums/ExpensePaymentTypeEnum'
import { mapExpenseTypeDataToFields } from './mapExpenseItemFormSchemaToItemPayload'
import { type ExpenseItemUpdatePayload } from '../types/ExpenseItemUpdatePayload'

interface MapExpenseItemFormSchemaToUpdatePayloadContext {
  itemId: number
  expenseReportId: number
}

const formatTaxPercentage = (item: ExpenseItemFormSchema): string => {
  // 'saved' (custom) tax preserves the Tax entity's raw decimal string;
  // percent and dollar modes serialize as a fixed-precision decimal for
  // the V3 endpoint.
  if (item.taxMode === 'saved') {
    return item.taxPercentage || '0'
  }
  return parseFloat(item.taxPercentage || '0').toFixed(8)
}

export const mapExpenseItemFormSchemaToUpdatePayload = (
  item: ExpenseItemFormSchema,
  { itemId, expenseReportId }: MapExpenseItemFormSchemaToUpdatePayloadContext
): ExpenseItemUpdatePayload => {
  let paymentType: number
  let creditcard: Nullable<number>

  if (item.reimbursable || item.paymentMethod?.id === OTHER_PAYMENT_OPTION_ID) {
    paymentType = ExpensePaymentTypes.OTHER
    creditcard = null
  } else {
    paymentType = item.paymentType
    creditcard = item.paymentMethod?.id ?? null
  }

  const computedSubtotal = parseFloat(item.subtotal || '0')
  const computedTaxAmount = parseFloat(item.taxAmount || '0')
  const computedTotal = parseFloat(item.amount || '0')

  return {
    id: itemId,
    merchant: item.merchant,
    account: item.account?.id ?? 0,
    expenseReport: expenseReportId,
    transDate: item.transactionDate,
    subtotal: parseFloat(computedSubtotal.toFixed(2)),
    amount: computedTotal.toFixed(2),
    reimburse: String(item.reimbursable),
    expense_type: item.expenseType,
    expense_type_fields: mapExpenseTypeDataToFields(item.expenseTypeData) ?? {},
    localCurrency: item.currency.id,
    currency_rate_final: item.currency.rate,
    tax_percentage: formatTaxPercentage(item),
    tax_amount: computedTaxAmount.toFixed(2),
    tax_name: item.taxName,
    tax_id: item.taxId,
    tax_is_inclusive: item.taxIsInclusive,
    paymentType,
    creditcard,
    attachment: item.attachment,
    notes: item.notes,
    exchange_rate_override: false,
    has_default_tax: false,
  }
}
